// DepositSection.tsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";

// Define the props interface
interface DepositSectionProps {
  onBack: () => void;
  onDeposit: (amount: string) => void;
}

const DepositSection: React.FC<DepositSectionProps> = ({
  onBack,
  onDeposit,
}) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedAmount, setSelectedAmount] = useState<string>("");
  const [showCustomAmount, setShowCustomAmount] = useState<boolean>(false);
  const [customAmount, setCustomAmount] = useState<string>("");

  // Predefined amount options
  const quickAmounts = ["10", "25", "50", "100", "200"];

  // Handle quick amount selection
  const handleAmountSelect = (amount: string) => {
    setSelectedAmount(amount);
    setShowCustomAmount(false);
    setCustomAmount("");
  };

  // Handle custom amount input
  const handleCustomAmountClick = () => {
    setShowCustomAmount(true);
    setSelectedAmount("");
  };

  // Handle numeric keypad input
  const handleNumberInput = (value: string) => {
    if (value === "<") {
      // Backspace functionality
      setCustomAmount((prev) => prev.slice(0, -1));
    } else if (value === ".") {
      // Add decimal point if not already present
      if (!customAmount.includes(".")) {
        setCustomAmount((prev) => prev + value);
      }
    } else {
      // Regular number input
      setCustomAmount((prev) => {
        // Prevent multiple leading zeros
        if (prev === "0" && value !== ".") {
          return value;
        }
        return prev + value;
      });
    }
  };

  // Handle deposit action
  const handleDeposit = () => {
    const amountToDeposit = showCustomAmount ? customAmount : selectedAmount;
    if (amountToDeposit && parseFloat(amountToDeposit) > 0) {
      onDeposit(amountToDeposit);
    }
  };

  // Render the main deposit screen (first image)
  const renderMainDepositSection = () => (
    <Box sx={{ p: isMobileView ? 2 : 3 }}>
      {/* Header */}
      {/* <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: "bold", flex: 1 }}>
          Add money
        </Typography>
        <IconButton onClick={() => setShowCustomAmount(true)}>
          <MoreVertIcon />
        </IconButton>
      </Box> */}

      {/* Subtitle */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, textAlign: "center" }}
      >
        Instantly transfer from a linked bank
      </Typography>

      {/* Quick Amount Selection */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
          justifyContent: "space-between",
        }}
      >
        {quickAmounts.map((amount) => (
          <Box
            key={amount}
            sx={{
              width: "calc(33.333% - 16px)",
              minWidth: "80px",
            }}
          >
            <Paper
              elevation={selectedAmount === amount ? 2 : 0}
              sx={{
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                border:
                  selectedAmount === amount
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  selectedAmount === amount
                    ? theme.palette.primary.light
                    : "background.paper",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              onClick={() => handleAmountSelect(amount)}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                ${amount}
              </Typography>
            </Paper>
          </Box>
        ))}
        <Box
          sx={{
            width: "calc(33.333% - 16px)",
            minWidth: "80px",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              textAlign: "center",
              cursor: "pointer",
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor: "transparent",
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={handleCustomAmountClick}
          >
            <Typography variant="h6" color="text.secondary">
              Other
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ mb: 3 }} />

      {/* Selected Amount Display */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Amount to add
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          ${selectedAmount || "0"}
        </Typography>
      </Box>

      {/* Deposit Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleDeposit}
        disabled={!selectedAmount}
        sx={{
          py: 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "1.1rem",
          fontWeight: "bold",
        }}
      >
        Add
      </Button>
    </Box>
  );

  // Render custom amount screen (second image)
  const renderCustomAmountScreen = () => (
    <Box sx={{ p: isMobileView ? 2 : 3 }}>
      {/* Header */}
      {/* <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => {
            setShowCustomAmount(false);
            setCustomAmount("");
          }}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: "bold", flex: 1 }}>
          Add money
        </Typography>
      </Box> */}

      {/* Amount Display */}
      <Box sx={{ textAlign: "center", mb: 2, mt: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1 }}>
          ${customAmount || "0"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter amount
        </Typography>
      </Box>

      {/* Numeric Keypad */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
          mb: 4,
        }}
      >
        {[
          { value: "1", display: "1" },
          { value: "2", display: "2" },
          { value: "3", display: "3" },
          { value: "4", display: "4" },
          { value: "5", display: "5" },
          { value: "6", display: "6" },
          { value: "7", display: "7" },
          { value: "8", display: "8" },
          { value: "9", display: "9" },
          { value: ".", display: "." },
          { value: "0", display: "0" },
          { value: "<", display: "<" },
        ].map((item) => (
          <Paper
            key={item.value}
            elevation={1}
            sx={{
              p: 0.5,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "background.paper",
              borderRadius: 1,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
              "&:active": {
                backgroundColor: theme.palette.action.selected,
              },
              aspectRatio: "1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "35px",
            }}
            onClick={() => handleNumberInput(item.value)}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color:
                  item.value === "<"
                    ? theme.palette.error.main
                    : "text.primary",
              }}
            >
              {item.display}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Deposit Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleDeposit}
        disabled={!customAmount || parseFloat(customAmount) <= 0}
        sx={{
          py: 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontSize: "1.1rem",
          fontWeight: "bold",
        }}
      >
        Add
      </Button>
    </Box>
  );

  const scrollableBoxStyles = {
    maxHeight: { xs: isMobileView ? "80vh" : "68vh", sm: "68vh" },
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  return (
    <Paper
      sx={{ ...scrollableBoxStyles }}
      // sx={{
      //   width: "100%",
      //   maxWidth: 400,
      //   margin: "auto",
      //   height: "100%",
      //   overflow: "auto",
      //   ...(isMobileView && {
      //     maxWidth: "100%",
      //     borderRadius: 0,
      //     boxShadow: "none",
      //   }),
      //   ...(!isMobileView && {
      //     borderRadius: 2,
      //     boxShadow: 3,
      //   }),
      // }}
    >
      {showCustomAmount
        ? renderCustomAmountScreen()
        : renderMainDepositSection()}
    </Paper>
  );
};

export default DepositSection;
